import axios from 'axios';
import env from '@/constants/env';
import useToastStore from '@/store/toast';
import { SITZUNG_SCHLUESSEL } from '@/store/callcenter/wizardSitzung';
import { bereinigeValidierungsAntwort, ersteOffeneFeldmeldung, istAmFeldGezeigt } from '@/utils/error';

const Network = axios.create({
  baseURL: env.baseUrl,
});

export const initializeAuthSession = (token?: string) => {
  clearInterceptors();
  Network.interceptors.request.use(
    (config) => {
      config.headers.Authorization = token ? `Bearer ${token}` : undefined;
      return config;
    },
    (error) => Promise.reject(error),
  );
};

export const clearInterceptors = () => {
  Network.interceptors.request.clear();
};

/**
 * Kurzname der Aktion fuer die 403-Meldung — „POST /meetings/12/outcome".
 *
 * WARUM ueberhaupt (Auftrag Alex 28.08.2026): der Toast sagte bisher nur
 * „Keine Berechtigung fuer diese Aktion". Damit ist eine Fehlberechtigung
 * praktisch nicht meldbar — der Berater sieht denselben Satz, egal ob ihm die
 * Sicht auf einen Termin, das Ergebnis-Setzen oder ein Export verweigert
 * wurde, und ein Support-Ticket „irgendwo kam keine Berechtigung" ist nicht
 * nachverfolgbar. Route und Verb sind das kleinste, was den Fall auffindbar
 * macht: sie zeigen direkt auf die Policy, die abgelehnt hat.
 *
 * 🔥 NUR PFAD, NIE QUERY. Ein Query-String traegt regelmaessig Suchbegriffe
 * und Filterwerte — bei der Kontaktsuche also personenbezogene Daten
 * (CLAUDE.md §1). Die landen weder im Toast noch in einem Screenshot davon.
 * Aus demselben Grund steht hier der ROHE Pfad aus der Axios-Config (`/deals/5`)
 * und nicht die vollstaendige URL samt Host.
 *
 * Faellt beides aus (kein `config`, keine `url`), bleibt der alte Wortlaut —
 * eine Meldung ohne Grund ist besser als eine mit „undefined".
 */
const aktionsName = (config?: { method?: string; url?: string }): string => {
  const pfad = (config?.url ?? '').split('?')[0];

  if (!pfad) return 'diese Aktion';

  const verb = (config?.method ?? '').toUpperCase();

  return verb ? `${verb} ${pfad}` : pfad;
};

Network.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const showToast = useToastStore.getState().showToast;

    if (!error.response) {
      showToast(
        'Netzwerkfehler. Bitte pruefen Sie Ihre Verbindung.',
        'error',
      );
      return Promise.reject(error);
    }

    const { status } = error.response;

    if (status === 401) {
      // Login-Endpoint gibt 401 bei falschen Credentials — nicht redirecten,
      // sondern Fehler normal an den Caller durchreichen
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/sign-in');
      if (!isAuthEndpoint) {
        localStorage.clear();
        // Auch den Wizard-Stand raeumen (07.08.2026, Reload-Fortsetzung).
        // Dieser Zweig ist ein Logout — nur laeuft er an `clearAllStates()`
        // vorbei, das den callcenter-Store sonst leert. Ohne diese Zeile bliebe
        // ein angefangener Vorgang mitsamt Kundendaten in `sessionStorage`
        // liegen, bis der Tab geschlossen wird; der naechste Login im selben
        // Tab ist genau der Fall „Logout+Login (selber Tab)" aus Bug 1
        // (31.05.2026). Der Stand ist zusaetzlich an die User-ID gebunden und
        // wuerde einem anderen Agenten nie angeboten — das hier ist der Gurt
        // daneben, damit die Daten gar nicht erst liegen bleiben.
        sessionStorage.removeItem(SITZUNG_SCHLUESSEL);
        window.location.href = '/auth/sign-in';
      }
    } else if (status === 403) {
      showToast(`Keine Berechtigung: ${aktionsName(error.config)}`, 'error');
    } else if (status === 422) {
      bereinigeValidierungsAntwort(error.response.data);
      // Structured meeting errors (error.code) are handled inline by the dialog — skip generic toast
      const isStructuredMeetingError = !!error.response?.data?.error?.code;
      if (!isStructuredMeetingError) {
        const felder = error.response.data?.errors;
        // Erst nach dem catch der Aufrufer entscheiden: wer die Meldung am Feld
        // zeigt, markiert den Fehler (utils/error.ts), dann bleibt es beim Feld.
        // Die Kette bis dorthin sind Mikrotasks und laeuft vor diesem Timer.
        setTimeout(() => {
          if (felder && Object.keys(felder).length > 0) {
            const offen = ersteOffeneFeldmeldung(error, felder);
            if (offen) showToast(offen, 'error');
          } else if (!istAmFeldGezeigt(error)) {
            showToast('Validierungsfehler. Bitte Eingaben pruefen.', 'error');
          }
        }, 0);
      }
    } else if (status === 500) {
      // Structured meeting errors (error.code) are handled inline by the dialog — skip generic toast
      const isStructuredMeetingError = !!error.response?.data?.error?.code;
      if (!isStructuredMeetingError) {
        showToast(
          'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es spaeter erneut.',
          'error',
        );
      }
    }

    return Promise.reject(error);
  },
);

export default Network;
